import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, eachDayOfInterval, startOfDay, isBefore } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import DeskGrid from '../components/DeskGrid';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { groupConsecutiveBookings } from '../utils/groupBookings';
import { CalendarDays, Loader2, Trash2, BookOpen, UserCheck } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { isAdmin } = useAuth();
  const { t, dateLocale } = useLanguage();

  const [desks, setDesks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [selectedDesk, setSelectedDesk] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [onBehalfOf, setOnBehalfOf] = useState('');
  const [showPastBookings, setShowPastBookings] = useState(false);
  const [deskFilter, setDeskFilter] = useState('all');

  const isWeekendOrHoliday = useCallback((date) => {
    const day = date.getDay();
    if (day === 0 || day === 6) return true;
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.includes(dateStr);
  }, [holidays]);

  const getSelectedDates = useCallback(() => {
    if (!dateRange.from) return [];
    if (!dateRange.to) return dateRange.from && !isWeekendOrHoliday(dateRange.from) ? [dateRange.from] : [];
    const allDays = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
    return allDays.filter(d => !isWeekendOrHoliday(d));
  }, [dateRange, isWeekendOrHoliday]);

  const fetchDesks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/desks`, { withCredentials: true });
      setDesks(data);
    } catch {
      toast.error(t('error'));
    }
  }, [t]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch {
      toast.error(t('error'));
    }
  }, [t]);

  const fetchAllBookings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/bookings`, { withCredentials: true });
      setAllBookings(data);
    } catch {
      toast.error(t('error'));
    }
  }, [t]);

  const fetchBookingsForDates = useCallback(async (dates) => {
    if (dates.length === 0) {
      setBookings([]);
      return;
    }
    try {
      const dateStrings = dates.map(d => format(d, 'yyyy-MM-dd'));
      const { data } = await axios.get(
        `${API_URL}/api/bookings/by-dates?dates=${dateStrings.join(',')}`,
        { withCredentials: true }
      );
      setBookings(data);
    } catch {
      toast.error(t('error'));
    }
  }, [t]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDesks(), fetchUsers(), fetchAllBookings(),
        axios.get(`${API_URL}/api/holidays`, { withCredentials: true })
          .then(({ data }) => setHolidays(data.holidays || []))
          .catch(() => {})
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDesks, fetchUsers, fetchAllBookings]);

  useEffect(() => {
    const dates = getSelectedDates();
    fetchBookingsForDates(dates);
    setSelectedDesk(null);
  }, [dateRange, fetchBookingsForDates, getSelectedDates]);

  const handleDateSelect = (range) => {
    setDateRange(range || { from: undefined, to: undefined });
  };

  const handleDeskClick = (deskId) => {
    setSelectedDesk(selectedDesk === deskId ? null : deskId);
  };

  const handleAdminBooking = async () => {
    const selectedDates = getSelectedDates();
    if (!selectedDesk || selectedDates.length === 0) {
      toast.error(t('selectPeriodAndDesk'));
      return;
    }

    setBookingLoading(true);
    try {
      const dateStrings = selectedDates.map(d => format(d, 'yyyy-MM-dd'));
      const payload = { desk_id: selectedDesk, dates: dateStrings };
      if (onBehalfOf && onBehalfOf !== 'self') {
        payload.on_behalf_of = onBehalfOf;
      }
      await axios.post(`${API_URL}/api/bookings`, payload, { withCredentials: true });
      toast.success(t('deskBlockedSuccess', { desk: selectedDesk, days: selectedDates.length }));
      setSelectedDesk(null);
      setDateRange({ from: undefined, to: undefined });
      setOnBehalfOf('');
      await Promise.all([fetchAllBookings(), fetchBookingsForDates([])]);
    } catch (e) {
      const errorMsg = e.response?.data?.detail || t('error');
      toast.error(errorMsg);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API_URL}/api/bookings/${bookingId}`, { withCredentials: true });
      toast.success(t('bookingCancelled'));
      await fetchAllBookings();
    } catch {
      toast.error(t('error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white rounded-full p-1 shadow-sm w-full overflow-x-auto flex-nowrap">
            <TabsTrigger
              value="overview"
              data-testid="tab-overview"
              className="rounded-full px-3 md:px-6 text-xs md:text-sm whitespace-nowrap data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <CalendarDays className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
              {t('overview')}
            </TabsTrigger>
            <TabsTrigger
              value="bookings"
              data-testid="tab-bookings"
              className="rounded-full px-3 md:px-6 text-xs md:text-sm whitespace-nowrap data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <BookOpen className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
              {t('bookings')}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 animate-fade-in-up">
            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="card-floating lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-xl font-extrabold text-slate-900">
                    {t('selectPeriod')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={handleDateSelect}
                    locale={dateLocale}
                    disabled={[{ before: startOfDay(new Date()) }, { dayOfWeek: [0, 6] }, ...holidays.map(h => new Date(h + 'T00:00:00'))]}
                    className="rounded-xl"
                    data-testid="admin-calendar"
                  />

                  {dateRange.from && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-xl">
                      <p className="text-sm font-semibold text-blue-700">
                        {t('selectedPeriodLabel')}
                      </p>
                      <p className="text-sm text-blue-600 mt-1">
                        {format(dateRange.from, 'dd MMMM yyyy', { locale: dateLocale })}
                        {dateRange.to && dateRange.to !== dateRange.from && (
                          <> → {format(dateRange.to, 'dd MMMM yyyy', { locale: dateLocale })}</>
                        )}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="card-floating lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-xl font-extrabold text-slate-900">
                    {t('deskMap')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DeskGrid
                    desks={desks}
                    bookings={bookings}
                    selectedDesk={selectedDesk}
                    onDeskClick={handleDeskClick}
                    isAdmin={isAdmin}
                    selectedDates={getSelectedDates()}
                  />

                  {selectedDesk && getSelectedDates().length > 0 && (
                    <div className="mt-6 p-4 bg-yellow-50 rounded-2xl space-y-3 animate-fade-in-up">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-yellow-900">
                            {t('blockDesk', { desk: selectedDesk })}
                          </p>
                          <p className="text-sm text-yellow-700">
                            {t('from')} {format(dateRange.from, 'dd/MM/yyyy', { locale: dateLocale })}
                            {dateRange.to && dateRange.to !== dateRange.from && (
                              <> {t('to')} {format(dateRange.to, 'dd/MM/yyyy', { locale: dateLocale })}</>
                            )}
                            {' '}({getSelectedDates().length} {getSelectedDates().length === 1 ? t('day') : t('days')})
                          </p>
                        </div>
                        <Button
                          onClick={handleAdminBooking}
                          disabled={bookingLoading}
                          data-testid="admin-block-desk-btn"
                          className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-full px-6 py-3 font-bold"
                        >
                          {bookingLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            t('blockDeskBtn')
                          )}
                        </Button>
                      </div>
                      {users.length > 0 && (
                        <div className="flex items-center gap-2 pt-2 border-t border-yellow-200">
                          <UserCheck className="w-4 h-4 text-yellow-700" />
                          <span className="text-xs font-semibold text-yellow-800">{t('bookOnBehalf')}:</span>
                          <Select value={onBehalfOf} onValueChange={setOnBehalfOf}>
                            <SelectTrigger className="flex-1 rounded-xl h-9 text-xs" data-testid="admin-on-behalf-select">
                              <SelectValue placeholder={t('selectUser')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="self">-- {t('selectUser')} --</SelectItem>
                              {users.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="animate-fade-in-up">
            <Card className="card-floating">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-xl font-extrabold text-slate-900">
                    {t('allBookings')}
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="show-past-admin"
                        checked={showPastBookings}
                        onCheckedChange={setShowPastBookings}
                        data-testid="admin-show-past-bookings-checkbox"
                      />
                      <label htmlFor="show-past-admin" className="text-sm text-slate-600 cursor-pointer select-none">
                        {t('showPastBookings')}
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-slate-600 whitespace-nowrap">{t('filterByDesk')}:</label>
                      <Select value={deskFilter} onValueChange={setDeskFilter}>
                        <SelectTrigger className="w-[140px] rounded-xl h-9 text-xs" data-testid="admin-desk-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('allDesks')}</SelectItem>
                          {desks.map(d => (
                            <SelectItem key={d.desk_id} value={String(d.desk_id)}>
                              {t('desk')} {d.desk_id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {allBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-500 font-medium">
                      {t('noBookingsFound')}
                    </p>
                  </div>
                ) : (() => {
                  const today = startOfDay(new Date());
                  const grouped = groupConsecutiveBookings(allBookings);
                  let filtered = showPastBookings
                    ? grouped
                    : grouped.filter(g => !isBefore(parseISO(g.end_date), today));
                  if (deskFilter !== 'all') {
                    filtered = filtered.filter(g => String(g.desk_id) === deskFilter);
                  }
                  const sorted = [...filtered].sort((a, b) => a.start_date.localeCompare(b.start_date));

                  return sorted.length === 0 ? (
                    <p className="text-center text-slate-400 py-8">{t('noBookingsFound')}</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sorted.map((group) => {
                        const isPast = isBefore(parseISO(group.end_date), today);
                        return (
                          <div
                            key={group.ids[0]}
                            data-testid={`admin-booking-group-${group.ids[0]}`}
                            className={`rounded-2xl p-4 ${
                              isPast
                                ? 'bg-slate-100'
                                : 'bg-gradient-to-br from-indigo-50 to-blue-50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className={`font-bold ${isPast ? 'text-slate-500' : 'text-slate-900'}`}>
                                  {t('desk')} {group.desk_id}
                                </p>
                                {group.start_date !== group.end_date ? (
                                  <div className={`text-sm ${isPast ? 'text-slate-400' : 'text-slate-600'}`}>
                                    <p>{t('from')}: {format(parseISO(group.start_date), 'EEE d MMM yyyy', { locale: dateLocale })}</p>
                                    <p>{t('to')}: {format(parseISO(group.end_date), 'EEE d MMM yyyy', { locale: dateLocale })}</p>
                                    <p className="text-xs mt-0.5 text-blue-500 font-semibold">{group.ids.length} {group.ids.length === 1 ? t('day') : t('days')}</p>
                                  </div>
                                ) : (
                                  <p className={`text-sm ${isPast ? 'text-slate-400' : 'text-slate-600'}`}>
                                    {format(parseISO(group.start_date), 'EEEE d MMMM yyyy', { locale: dateLocale })}
                                  </p>
                                )}
                                <p className={`text-sm mt-1 font-medium ${isPast ? 'text-slate-400' : 'text-indigo-600'}`}>
                                  {group.user_name} ({group.user_email})
                                </p>
                                {isPast && (
                                  <span className="text-xs text-slate-400">{t('past')}</span>
                                )}
                              </div>
                              {!isPast && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  data-testid={`admin-cancel-${group.ids[0]}`}
                                  onClick={async () => {
                                    for (const id of group.ids) await handleCancelBooking(id);
                                  }}
                                  className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-full"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
